<template>
  <div class="tw:relative">
    <div
      v-if="readOnly"
      class="tw:absolute tw:left-0 tw:top-0 tw:h-full tw:w-full tw:cursor-not-allowed"
      data-testid="readonly"
    >
      <StreemTooltip
        anchor="center right"
        self="center left"
      >
        {{ getLocaleText("source_filters.read_only") }}
      </StreemTooltip>
    </div>
    <BaseExpansionPanel
      bordered
      data-testid="keywords"
      :default-opened="!readOnly"
    >
      <template #header="{ toggle }">
        <div
          class="tw:flex tw:flex-1 tw:items-center tw:gap-2 tw:px-4 tw:py-4"
          @click="toggle"
        >
          {{ getLocaleText("source_filters.keyword_title") }}

          <Tag
            v-if="keywordFilters.length"
            color="secondary"
            hide-cursor
            hide-remove-button
            :label="keywordFilters.length"
            rounded
          />
        </div>
      </template>
      <div class="tw:-mt-2 tw:flex tw:flex-col tw:gap-2">
        <div>
          <InputText
            v-model="keyword"
            new-design
            :placeholder="getLocaleText('source_filters.keyword_placeholder')"
            :validations="[mininumLength]"
            @keydown.enter.stop="enterKeyword"
          />
        </div>
        <template v-if="keywordFilters.length">
          <div class="tw:flex tw:flex-wrap tw:gap-2">
            <Tag
              v-for="(filter, filterIndex) in keywordFilters"
              :key="filterIndex"
              color="secondary"
              hide-cursor
              :label="filter.value as string"
              rounded
              @remove="removeFilter(filter)"
            />
          </div>
          <div>
            <BaseButton
              color="form"
              dense
              flat
              :label="getLocaleText('source_filters.clear_all')"
              new-design
              @click="clearFilters('keyword')"
            />
          </div>
        </template>
      </div>
    </BaseExpansionPanel>

    <SourceFiltersLocation
      class="tw:mt-2"
      data-testid="locations"
    >
      <template #header="{ toggle }">
        <div
          class="tw:flex tw:flex-1 tw:items-center tw:gap-2 tw:px-4 tw:py-3"
          @click="toggle"
        >
          {{ getLocaleText("source_filters.location_title") }}

          <Tag
            v-if="selectedLocations.length"
            color="secondary"
            hide-cursor
            hide-remove-button
            :label="selectedLocations.length"
            rounded
          />
        </div>
      </template>

      <template
        v-if="selectedLocations.length"
        #footer
      >
        <div class="tw:mt-2">
          <BaseButton
            color="form"
            dense
            flat
            :label="getLocaleText('source_filters.clear_all')"
            new-design
            @click="clearFilters('location')"
          />
        </div>
      </template>
    </SourceFiltersLocation>

    <BaseExpansionPanel
      v-if="isCisionOne"
      bordered
      class="tw:mt-2"
      data-testid="sectors"
    >
      <template #header="{ toggle }">
        <div
          class="tw:flex tw:flex-1 tw:items-center tw:gap-2 tw:px-4 tw:py-3"
          @click="toggle"
        >
          {{ getLocaleText("source_filters.sector_title") }}

          <Tag
            v-if="selectedTopics.length"
            color="secondary"
            hide-cursor
            hide-remove-button
            :label="selectedTopics.length"
            rounded
          />
        </div>
      </template>
      <div class="tw:-mt-2 tw:flex tw:flex-col tw:gap-2">
        <SourceFiltersTopics />
        <template v-if="selectedTopics.length">
          <div class="tw:flex tw:flex-wrap tw:gap-2">
            <Tag
              v-for="(filter, filterIndex) in selectedTopics"
              :key="filterIndex"
              color="secondary"
              hide-cursor
              :label="(filter.value as FilterValueItem).name"
              rounded
              @remove="removeFilter(filter)"
            />
          </div>
          <div>
            <BaseButton
              color="form"
              dense
              flat
              :label="getLocaleText('source_filters.clear_all')"
              new-design
              @click="clearFilters('topic')"
            />
          </div>
        </template>
      </div>
    </BaseExpansionPanel>

    <SourceFiltersCategory
      v-if="!isCisionOne"
      class="tw:mt-2"
      data-testid="categories"
    >
      <template #header="{ toggle }">
        <div
          class="tw:flex tw:flex-1 tw:items-center tw:gap-2 tw:px-4 tw:py-3"
          @click="toggle"
        >
          {{ getLocaleText("source_filters.category_title") }}

          <Tag
            v-if="selectedCategories.length"
            color="secondary"
            hide-cursor
            hide-remove-button
            :label="selectedCategories.length"
            rounded
          />
        </div>
      </template>
      <template
        v-if="selectedCategories.length"
        #footer
      >
        <div class="tw:mt-2">
          <BaseButton
            color="form"
            dense
            flat
            :label="getLocaleText('source_filters.clear_all')"
            new-design
            @click="clearFilters('category')"
          />
        </div>
      </template>
    </SourceFiltersCategory>

    <BaseExpansionPanel
      bordered
      class="tw:mt-2"
      data-testid="source-groups"
    >
      <template #header="{ toggle }">
        <div
          class="tw:flex tw:flex-1 tw:items-center tw:gap-2 tw:px-4 tw:py-3"
          @click="toggle"
        >
          {{ getLocaleText("source_filters.source_group_title") }}

          <Tag
            v-if="selectedSourceGroups.length"
            color="secondary"
            hide-cursor
            hide-remove-button
            :label="selectedSourceGroups.length"
            rounded
          />
        </div>
      </template>
      <div class="tw:-mt-2 tw:flex tw:flex-col tw:gap-2">
        <SourceFiltersGroups />
        <template v-if="selectedSourceGroups.length">
          <div class="tw:flex tw:flex-wrap tw:gap-2">
            <Tag
              v-for="(filter, filterIndex) in selectedSourceGroups"
              :key="filterIndex"
              color="secondary"
              hide-cursor
              :label="(filter.value as FilterValueItem).name"
              rounded
              @remove="removeFilter(filter)"
            />
          </div>
          <div>
            <BaseButton
              color="form"
              dense
              flat
              :label="getLocaleText('source_filters.clear_all')"
              new-design
              @click="clearFilters('group')"
            />
          </div>
        </template>
      </div>
    </BaseExpansionPanel>

    <BaseExpansionPanel
      bordered
      class="tw:mt-2"
      data-testid="media"
    >
      <template #header="{ toggle }">
        <div
          class="tw:flex tw:flex-1 tw:items-center tw:gap-2 tw:px-4 tw:py-3"
          @click="toggle"
        >
          {{ getLocaleText("source_filters.media_type_title") }}

          <Tag
            v-if="mediaFilters.length"
            color="secondary"
            hide-cursor
            hide-remove-button
            :label="mediaFilters.length"
            rounded
          />
        </div>
      </template>
      <div class="tw:-mt-2 tw:flex tw:flex-col tw:gap-2">
        <div class="tw:flex tw:flex-col tw:gap-2">
          <Checkbox
            dense
            :indeterminate="mediaIndeterminate"
            :label="getLocaleText('source_filters.select_all')"
            :model-value="allMedia"
            new-design
            @update:model-value="toggleMedia"
          />
          <MediumSelector
            v-model="media"
            class="tw:gap-y-2"
            :columns-per-row="2"
            dense
            :excluded-media="excludedMedia"
          />
        </div>
        <div v-if="mediaFilters.length">
          <BaseButton
            color="form"
            dense
            flat
            :label="getLocaleText('source_filters.clear_all')"
            new-design
            @click="clearFilters('medium')"
          />
        </div>
      </div>
    </BaseExpansionPanel>

    <BaseExpansionPanel
      v-if="!isCisionOne && availableContentTypeFilters.length"
      bordered
      class="tw:mt-2"
      data-testid="content-types"
    >
      <template #header="{ toggle }">
        <div
          class="tw:flex tw:flex-1 tw:items-center tw:gap-2 tw:px-4 tw:py-3"
          @click="toggle"
        >
          {{ getLocaleText("source_filters.content_type_title") }}

          <Tag
            v-if="selectedContentTypes.length"
            color="secondary"
            hide-cursor
            hide-remove-button
            :label="selectedContentTypes.length"
            rounded
          />
        </div>
      </template>
      <div class="tw:-mt-2 tw:flex tw:flex-col tw:gap-2">
        <div class="tw:flex tw:flex-col tw:gap-2">
          <Checkbox
            dense
            :indeterminate="contentTypesIndeterminate"
            :label="getLocaleText('source_filters.select_all')"
            :model-value="allContentTypes"
            new-design
            @update:model-value="toggleContentTypes"
          />
          <Checkbox
            v-for="filter in availableContentTypeFilters"
            :key="filter.filter"
            dense
            :label="filter.label"
            :model-value="contentTypeFilters[filter.filter]"
            new-design
            @update:model-value="toggleContentType(filter)"
          />
        </div>
        <div v-if="selectedContentTypes.length">
          <BaseButton
            color="form"
            dense
            flat
            :label="getLocaleText('source_filters.clear_all')"
            new-design
            @click="clearFilters('content')"
          />
        </div>
      </div>
    </BaseExpansionPanel>

    <div
      v-if="showEditStream"
      class="tw:mt-4"
    >
      <div class="tw:text-denim-600">
        {{ getLocaleText("source_filters.edit_stream_heading") }}
      </div>
      <div class="tw:mt-1">
        <a
          class="tw:cursor-pointer tw:text-blue-600 tw:hover:underline"
          data-testid="edit-stream"
          @click="editStream"
        >
          {{ getLocaleText("source_filters.edit_stream_label") }}
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, ref, watch } from "vue";

import { track } from "shared/boot/analytics";
import { getLocaleText } from "shared/boot/i18n";
import {
  BaseButton,
  BaseExpansionPanel,
  InputText,
  Tag,
} from "shared/components/base";
import StreemTooltip from "shared/components/base/StreemTooltip.vue";
import Checkbox from "shared/components/core/forms/Checkbox.vue";
import MediumSelector from "shared/components/selectors/MediumSelector.vue";
import useSourceFilters, {
  type ContentTypeFilter,
} from "shared/composables/useSourceFilters";
import { streamTypes } from "shared/constants";
import type { MediumName } from "shared/helpers/media";
import useGlobalStore from "shared/stores/global";
import type { FilterValueItem } from "shared/stores/sources";
import type { Nullable, Stream } from "shared/types";

import SourceFiltersCategory from "./SourceFiltersCategory.vue";
import SourceFiltersGroups from "./SourceFiltersGroups.vue";
import SourceFiltersLocation from "./SourceFiltersLocation.vue";
import SourceFiltersTopics from "./SourceFiltersTopics.vue";

export interface SourceFiltersProps {
  stream?: Nullable<Stream>;
  readOnly?: boolean;
  editingStream?: boolean;
  enabledMedia?: MediumName[];
  minimumKeywordCharacters?: number;
}

const props = withDefaults(defineProps<SourceFiltersProps>(), {
  stream: null,
  enabledMedia: () => [],
  minimumKeywordCharacters: 3,
});

const emit = defineEmits(["edit-stream"]);

const {
  keywordFilters,
  selectedCategories,
  selectedLocations,
  media,
  mediaFilters,
  validMedia,
  excludedMedia,
  selectedTopics,
  selectedSourceGroups,
  selectedContentTypes,
  availableContentTypeFilters,
  contentTypeFilters,
  totalFilters,
  clearFilters,
  removeFilter,
  addFilter,
  addFilters,
  toggleFilter,
} = useSourceFilters({
  stream: props.stream,
  enabledMedia: props.enabledMedia,
});

const globalStore = useGlobalStore();

const { isCisionOne } = storeToRefs(globalStore);

const keyword = ref("");
const allMedia = ref(false);
const allContentTypes = ref(false);

const mediaIndeterminate = computed<boolean>(() => {
  if (!mediaFilters.value.length) return false;

  return mediaFilters.value.length !== validMedia.value.length;
});

const contentTypesIndeterminate = computed<boolean>(() => {
  if (!selectedContentTypes.value.length) return false;

  return (
    selectedContentTypes.value.length !==
    availableContentTypeFilters.value.length
  );
});

const showEditStream = computed<boolean>(() =>
  Boolean(
    props.stream?.can_modify &&
      props.stream?.type === streamTypes.mentionStream &&
      totalFilters.value &&
      !props.editingStream
  )
);

function enterKeyword(): void {
  if (keyword.value.length < 3) return;

  track("Add keywords in Advanced Source Filter");

  addFilter({
    type: "keyword",
    value: keyword.value,
  });

  keyword.value = "";
}

function toggleMedia(): void {
  allMedia.value = !allMedia.value;

  if (allMedia.value) {
    addFilters("medium", validMedia.value);
  } else {
    clearFilters("medium");
  }
}

function toggleContentTypes(): void {
  allContentTypes.value = !allContentTypes.value;

  if (allContentTypes.value) {
    addFilters(
      "content",
      availableContentTypeFilters.value.map((filter) => filter.filter)
    );

    track("Add content type in Advanced Source Filter");
  } else {
    clearFilters("content");
  }
}

function toggleContentType(filter: ContentTypeFilter): void {
  toggleFilter({
    type: "content",
    value: filter.filter,
  });
}

function mininumLength(value: string): string | boolean {
  if (value && value.length < 3) {
    return getLocaleText("source_filters.character_limit", {
      characters: props.minimumKeywordCharacters,
    });
  }

  return true;
}

function editStream(): void {
  emit("edit-stream");
}

watch(mediaFilters, () => {
  allMedia.value = mediaFilters.value.length === validMedia.value.length;
});

watch(selectedContentTypes, () => {
  allContentTypes.value =
    selectedContentTypes.value.length ===
    availableContentTypeFilters.value.length;
});
</script>
